import React, { useState, useEffect } from 'react';
import { learningHooksApi } from '../../services/api';

interface Student {
  id: number;
  email: string;
  name: string;
  first_name: string;
  last_name: string;
  grade_level: string;
  student_id: string;
  enrolled_at: string;
  is_active: boolean;
}

interface LearningHook {
  id: number;
  title: string;
  grade_levels: string;
  enrolled_count: number;
}

interface AddStudentForm {
  first_name: string;
  last_name: string;
  email: string;
  grade_level: string;
  student_id: string;
  hook_id: number | null;
}

export const StudentManagement: React.FC = () => {
  const [hooks, setHooks] = useState<LearningHook[]>([]);
  const [selectedHook, setSelectedHook] = useState<number | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Add student modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState<AddStudentForm>({
    first_name: '',
    last_name: '',
    email: '',
    grade_level: '',
    student_id: '',
    hook_id: null
  });
  
  // Edit student modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    grade_level: '',
    student_id: ''
  });
  
  // Transfer modal
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState<number[]>([]);
  const [transferToHook, setTransferToHook] = useState<number | null>(null);

  // Enroll existing students modal
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [availableStudents, setAvailableStudents] = useState<Student[]>([]);
  const [gradeFilter, setGradeFilter] = useState<string>('');
  const [selectedToEnroll, setSelectedToEnroll] = useState<number[]>([]);

  useEffect(() => {
    fetchHooks();
  }, []);

  useEffect(() => {
    if (selectedHook) {
      fetchStudents(selectedHook);
    }
  }, [selectedHook]);

  const fetchHooks = async () => {
    try {
      const response = await learningHooksApi.getAll();
      // Handle the response structure: {"learning_hooks": [...]}
      const hooksData = response.data.learning_hooks || response.data.results || response.data || [];
      setHooks(Array.isArray(hooksData) ? hooksData : []);
    } catch (err: any) {
      setError('Failed to load learning hooks');
    }
  };

  const fetchStudents = async (hookId: number) => {
    setLoading(true);
    try {
      const response = await learningHooksApi.getStudents(hookId);
      setStudents(response.data.students || []);
      setError('');
    } catch (err: any) {
      setError('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudent = async () => {
    if (!addForm.first_name || !addForm.last_name || !addForm.email || !addForm.grade_level) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const data: any = { ...addForm };
      if (selectedHook) {
        data.hook_id = selectedHook;
      }
      
      const response = await learningHooksApi.addStudent(data);
      setSuccess('Student added successfully!');
      setShowAddModal(false);
      setAddForm({
        first_name: '',
        last_name: '',
        email: '',
        grade_level: '',
        student_id: '',
        hook_id: null
      });
      
      if (selectedHook) {
        await fetchStudents(selectedHook);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add student');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveStudent = async (studentId: number) => {
    if (!selectedHook) return;
    
    if (!confirm('Are you sure you want to remove this student from the learning hook?')) {
      return;
    }

    setLoading(true);
    try {
      await learningHooksApi.removeStudent(studentId, selectedHook);
      setSuccess('Student removed from learning hook successfully!');
      await fetchStudents(selectedHook);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove student');
    } finally {
      setLoading(false);
    }
  };

  const handleEditStudent = async () => {
    if (!editingStudent) return;

    setLoading(true);
    try {
      await learningHooksApi.updateStudent(editingStudent.id, editForm);
      setSuccess('Student information updated successfully!');
      setShowEditModal(false);
      setEditingStudent(null);
      
      if (selectedHook) {
        await fetchStudents(selectedHook);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update student');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (student: Student) => {
    setEditingStudent(student);
    setEditForm({
      first_name: student.first_name,
      last_name: student.last_name,
      email: student.email,
      grade_level: student.grade_level,
      student_id: student.student_id
    });
    setShowEditModal(true);
  };

  const handleTransferStudents = async () => {
    if (!selectedHook || !transferToHook || selectedStudents.length === 0) {
      setError('Please select students and destination learning hook');
      return;
    }

    setLoading(true);
    try {
      await learningHooksApi.transferStudents({
        from_hook_id: selectedHook,
        to_hook_id: transferToHook,
        student_ids: selectedStudents
      });
      
      setSuccess(`${selectedStudents.length} students transferred successfully!`);
      setShowTransferModal(false);
      setSelectedStudents([]);
      setTransferToHook(null);
      
      await fetchStudents(selectedHook);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to transfer students');
    } finally {
      setLoading(false);
    }
  };

  const toggleStudentSelection = (studentId: number) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const fetchAvailableStudents = async (grade?: string) => {
    if (!selectedHook) return;
    try {
      const response = await learningHooksApi.getAvailableStudents(grade, selectedHook);
      setAvailableStudents(response.data.students || []);
    } catch (err: any) {
      setError('Failed to load available students');
    }
  };

  const handleOpenEnrollModal = () => {
    setShowEnrollModal(true);
    setGradeFilter('');
    setSelectedToEnroll([]);
    fetchAvailableStudents();
  };

  const handleGradeFilterChange = (grade: string) => {
    setGradeFilter(grade);
    fetchAvailableStudents(grade || undefined);
  };

  const toggleEnrollSelection = (studentId: number) => {
    setSelectedToEnroll(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleEnrollExisting = async () => {
    if (!selectedHook || selectedToEnroll.length === 0) return;
    
    setLoading(true);
    try {
      await learningHooksApi.enrollExisting(selectedHook, selectedToEnroll);
      setSuccess(`${selectedToEnroll.length} student(s) enrolled successfully!`);
      setShowEnrollModal(false);
      setSelectedToEnroll([]);
      await fetchStudents(selectedHook);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to enroll students');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold mb-6">Student Management</h2>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-6">
          {success}
        </div>
      )}

      {/* Learning Hook Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Learning Hook
        </label>
        {hooks.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
            <p className="font-medium">No Learning Hooks Available</p>
            <p className="text-sm mt-1">Please create a Learning Hook first in the "Learning Hooks" tab before managing students.</p>
          </div>
        ) : (
          <select
            value={selectedHook || ''}
            onChange={(e) => setSelectedHook(Number(e.target.value))}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Choose a learning hook...</option>
            {hooks.map((hook: LearningHook) => (
              <option key={hook.id} value={hook.id}>
                {hook.title} ({hook.enrolled_count} enrolled)
              </option>
            ))}
          </select>
        )}
      </div>

      {selectedHook && (
        <>
          {/* Actions */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              ➕ Add Student
            </button>
            
            <button
              onClick={handleOpenEnrollModal}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
            >
              📋 Enroll Existing Students
            </button>
            
            {selectedStudents.length > 0 && (
              <button
                onClick={() => setShowTransferModal(true)}
                className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
              >
              🔄 Transfer {selectedStudents.length} Student{selectedStudents.length > 1 ? 's' : ''}
              </button>
            )}
          </div>

          {/* Students List */}
          {loading ? (
            <div className="text-center py-8">Loading students...</div>
          ) : students.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No students enrolled in this learning hook
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedStudents.length === students.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStudents(students.map(s => s.id));
                          } else {
                            setSelectedStudents([]);
                          }
                        }}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Grade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Enrolled
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {students.map(student => (
                    <tr key={student.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(student.id)}
                          onChange={() => toggleStudentSelection(student.id)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{student.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{student.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {student.grade_level}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {student.student_id || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(student.enrolled_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => openEditModal(student)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleRemoveStudent(student.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Add Student Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4">Add Student</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input
                  type="text"
                  value={addForm.first_name}
                  onChange={(e) => setAddForm({...addForm, first_name: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  value={addForm.last_name}
                  onChange={(e) => setAddForm({...addForm, last_name: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={addForm.email}
                  onChange={(e) => setAddForm({...addForm, email: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grade Level *</label>
                <select
                  value={addForm.grade_level}
                  onChange={(e) => setAddForm({...addForm, grade_level: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select grade...</option>
                  <option value="6th">6th Grade</option>
                  <option value="7th">7th Grade</option>
                  <option value="8th">8th Grade</option>
                  <option value="9th">9th Grade</option>
                  <option value="10th">10th Grade</option>
                  <option value="11th">11th Grade</option>
                  <option value="12th">12th Grade</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Student ID</label>
                <input
                  type="text"
                  value={addForm.student_id}
                  onChange={(e) => setAddForm({...addForm, student_id: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="Optional"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddStudent}
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
              >
                {loading ? 'Adding...' : 'Add Student'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Student Modal */}
      {showEditModal && editingStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4">Edit Student</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grade Level</label>
                <select
                  value={editForm.grade_level}
                  onChange={(e) => setEditForm({...editForm, grade_level: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="6th">6th Grade</option>
                  <option value="7th">7th Grade</option>
                  <option value="8th">8th Grade</option>
                  <option value="9th">9th Grade</option>
                  <option value="10th">10th Grade</option>
                  <option value="11th">11th Grade</option>
                  <option value="12th">12th Grade</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Student ID</label>
                <input
                  type="text"
                  value={editForm.student_id}
                  onChange={(e) => setEditForm({...editForm, student_id: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditStudent}
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update Student'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4">
              Transfer {selectedStudents.length} Student{selectedStudents.length > 1 ? 's' : ''}
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Moving {selectedStudents.length} student{selectedStudents.length > 1 ? 's' : ''} from current learning hook to:
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Destination Learning Hook</label>
              <select
                value={transferToHook || ''}
                onChange={(e) => setTransferToHook(Number(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="">Select destination learning hook...</option>
                {hooks
                  .filter(h => h.id !== selectedHook)
                  .map(hook => (
                    <option key={hook.id} value={hook.id}>
                      {hook.title}
                    </option>
                  ))}
              </select>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowTransferModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleTransferStudents}
                disabled={loading || !transferToHook}
                className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 disabled:opacity-50"
              >
                {loading ? 'Transferring...' : 'Transfer Students'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enroll Existing Students Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">Enroll Existing Students</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Grade Level</label>
              <select
                value={gradeFilter}
                onChange={(e) => handleGradeFilterChange(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="">All Grades</option>
                <option value="6th">6th Grade</option>
                <option value="7th">7th Grade</option>
                <option value="8th">8th Grade</option>
                <option value="9th">9th Grade</option>
                <option value="10th">10th Grade</option>
                <option value="11th">11th Grade</option>
                <option value="12th">12th Grade</option>
              </select>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                {availableStudents.length} student(s) available • {selectedToEnroll.length} selected
              </p>
            </div>
            
            {availableStudents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No available students found{gradeFilter ? ` for ${gradeFilter} grade` : ''}
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto border rounded-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                        <input
                          type="checkbox"
                          checked={selectedToEnroll.length === availableStudents.length && availableStudents.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedToEnroll(availableStudents.map(s => s.id));
                            } else {
                              setSelectedToEnroll([]);
                            }
                          }}
                        />
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Grade</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {availableStudents.map(student => (
                      <tr key={student.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <input
                            type="checkbox"
                            checked={selectedToEnroll.includes(student.id)}
                            onChange={() => toggleEnrollSelection(student.id)}
                          />
                        </td>
                        <td className="px-4 py-2 text-sm">{student.name}</td>
                        <td className="px-4 py-2 text-sm text-gray-500">{student.email}</td>
                        <td className="px-4 py-2 text-sm">{student.grade_level}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowEnrollModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEnrollExisting}
                disabled={loading || selectedToEnroll.length === 0}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50"
              >
                {loading ? 'Enrolling...' : `Enroll ${selectedToEnroll.length} Student(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
